/**
 * Client-side mirror of backend User::withinShiftNow().
 * - Admins: never locked
 * - Missing start OR end: no limit
 * - Overnight windows (e.g. 17:00 → 03:00) wrap midnight
 */

const readSessionUser = () => {
  try {
    return JSON.parse(localStorage.getItem("sessionUser") || "null") || {};
  } catch {
    return {};
  }
};

const normalizeClock = (clock) => {
  const s = String(clock ?? "").trim();
  return s === "" || s === "null" || s === "undefined" ? "" : s;
};

const toMinutes = (clock) => {
  const s = normalizeClock(clock);
  if (!s) return null;
  const parts = s.split(":");
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10) || 0;
  if (!Number.isFinite(h) || h < 0 || h > 23) return null;
  return h * 60 + m;
};

export function isAdminUser(user = readSessionUser()) {
  return String(user?.role || "").toLowerCase() === "admin";
}

export function hasShiftConfigured(user = readSessionUser()) {
  if (isAdminUser(user)) return false;
  const start = normalizeClock(user?.shift_start);
  const end = normalizeClock(user?.shift_end);
  return Boolean(start && end);
}

/** True when the user may mutate data right now. */
export function withinShiftNow(user = readSessionUser(), now = new Date()) {
  if (isAdminUser(user)) return true;
  if (!hasShiftConfigured(user)) return true;
  const start = toMinutes(user?.shift_start);
  const end = toMinutes(user?.shift_end);
  if (start == null || end == null) return true;
  if (start === end) return true;
  const mins = now.getHours() * 60 + now.getMinutes();
  return start < end ? mins >= start && mins < end : mins >= start || mins < end;
}

export function shiftLabel(user = readSessionUser()) {
  if (!hasShiftConfigured(user)) return "";
  return `${normalizeClock(user.shift_start)}–${normalizeClock(user.shift_end)}`;
}

/** API paths still allowed outside shift (must match backend allowlist). */
export function isShiftEscapeUrl(url = "") {
  const path = String(url || "").split("?")[0].replace(/^\/+/, "");
  const bare = path.replace(/^api\//, "");
  return bare === "logout" || bare === "pos/register/close" || bare.endsWith("/logout") || bare.endsWith("/pos/register/close");
}

export function outsideShiftError(user = readSessionUser()) {
  const hours = shiftLabel(user) || "—";
  const err = new Error(
    `Outside your working hours (${hours}). You cannot sell, return, edit, cancel, or delete right now.`
  );
  err.code = "OUTSIDE_SHIFT";
  err.response = {
    status: 403,
    data: { code: "OUTSIDE_SHIFT", message: err.message },
  };
  return err;
}
